import React, { useState, useEffect, useMemo } from "react";
import { Plus, CreditCard, MoreVertical, Search, ArrowUpDown, ArrowUp, ArrowDown, Edit2, Trash2, Smartphone, Building2, Banknote, RefreshCw } from "lucide-react";
import PaymentAccountsForm from "./PaymentAccountsForm";
import usePaymentAccounts from "../../../hooks/usePaymentAccounts";
import { ConfirmDialog } from "../Menu_Management/UIComponents";

export default function SettingsManagement({ renderActions }) {
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const {
    accounts,
    createAccount,
    updateAccount,
    deleteAccount,
    refreshData,
    loading,
  } = usePaymentAccounts();

  // Derive branches from accounts data
  const branches = useMemo(() => {
    const map = new Map();
    accounts.forEach(acc => {
      if (acc.branchId && !map.has(acc.branchId)) {
        map.set(acc.branchId, { id: acc.branchId, name: acc.branchName });
      }
    });
    const list = Array.from(map.values());
    return list.length > 1 ? list : [];
  }, [accounts]);

  // Auto-select first branch when branches change
  useEffect(() => {
    if (branches.length > 1 && !selectedBranchId) {
      setSelectedBranchId(branches[0].id);
    }
    if (branches.length <= 1) {
      setSelectedBranchId(null);
    }
  }, [branches]);
  const [confirmDialog, setConfirmDialog] = useState({
  isOpen: false,
  data: null,
});


  const handleSave = async (data) => {
    try {
      if (editItem) {
        await updateAccount(editItem.id, data);
      } else {
        await createAccount(data);
      }
      setShowForm(false);
      setEditItem(null);
    } catch (error) {
      console.error(" Failed to save payment account:", error);
    }
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest(".action-menu-container")) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

// header actions registration
useEffect(() => {
  if (renderActions) {
    renderActions(actions);
  }
}, []);


const handleDeleteClick = (account) => {
  setConfirmDialog({
    isOpen: true,
    data: account,
  });
};

const deleteNow = async () => {
  if (!confirmDialog.data) return;

  try {
    await deleteAccount(confirmDialog.data.id);
  } catch (err) {
    console.error("Delete failed", err);
  } finally {
    setConfirmDialog({ isOpen: false, data: null });
    setOpenActionMenu(null);
  }
};


  // Filter accounts based on branch and search text
  const filteredAccounts = useMemo(() => {
    let result = [...accounts];

    // Branch filter
    if (selectedBranchId) {
      result = result.filter(acc => acc.branchId === selectedBranchId);
    }

    // Search filter
    if (filterText) {
      const text = filterText.toLowerCase();
      result = result.filter(acc => {
        const statusText = acc.is_active ? "active" : "inactive";
        const defaultText = acc.is_default ? "yes" : "no";
        return (
          acc.account_name?.toLowerCase().includes(text) ||
          acc.account_type?.toLowerCase().includes(text) ||
          acc.upi_id?.toLowerCase().includes(text) ||
          acc.bank_name?.toLowerCase().includes(text) ||
          acc.account_number?.toString().includes(text) ||
          statusText.includes(text) ||
          defaultText.includes(text)
        );
      });
    }

    return result;
  }, [accounts, selectedBranchId, filterText]);

  // Sort accounts
  const sortedAccounts = React.useMemo(() => {
    let sortableAccounts = [...filteredAccounts];

    if (sortConfig.key !== null) {
      sortableAccounts.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle boolean values
        if (typeof aValue === 'boolean') {
          aValue = aValue ? 1 : 0;
          bValue = bValue ? 1 : 0;
        }

        // Handle null/undefined
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';

        // Convert to string for comparison
        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableAccounts;
  }, [filteredAccounts, sortConfig]);

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={14} className="text-slate-400" />;
    }
    return sortConfig.direction === 'asc'
      ? <ArrowUp size={14} className="text-slate-700" />
      : <ArrowDown size={14} className="text-slate-700" />;
  };

  // Get account type icon
  const getAccountTypeIcon = (type) => {
    switch(type) {
      case 'upi':
        return <Smartphone size={16} className="text-purple-600" />;
      case 'bank':
        return <Building2 size={16} className="text-blue-600" />;
      case 'card_machine':
        return <CreditCard size={16} className="text-teal-600" />;
      case 'cash_counter':
        return <Banknote size={16} className="text-emerald-600" />;
      default:
        return <CreditCard size={16} className="text-slate-600" />;
    }
  };

  // Actions for header
  const actions = {
    onAdd: () => {
      setShowForm(true);
      setEditItem(null);
    },
    onRefresh: refreshData,
  };

  // Call renderActions if provided (for putting buttons in header)
  React.useEffect(() => {
    if (renderActions) {
      renderActions(actions);
    }
  }, [renderActions]);

  return (
    <div className="p-4">
      <div className="w-full">
        {/* Branch Tabs */}
        {branches.length > 1 && (
          <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 mb-4">
            {branches.map(b => (
              <button
                key={b.id}
                onClick={() => {
                  setSelectedBranchId(b.id);
                  setFilterText('');
                }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedBranchId === b.id
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Search by account name, type, status, or details..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 bg-white border-2 border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400 transition-all"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th
                    onClick={() => handleSort('account_name')}
                    className="text-left px-6 py-4 text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Account Name
                      {getSortIcon('account_name')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('account_type')}
                    className="text-left px-6 py-4 text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Type
                      {getSortIcon('account_type')}
                    </div>
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Details
                  </th>
                  <th
                    onClick={() => handleSort('is_active')}
                    className="text-left px-6 py-4 text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Status
                      {getSortIcon('is_active')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('is_default')}
                    className="text-left px-6 py-4 text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Default
                      {getSortIcon('is_default')}
                    </div>
                  </th>
                  <th className="text-right px-6 py-4 text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td className="px-6 py-12 text-center text-slate-500" colSpan={6}>
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium">Loading accounts...</p>
                      </div>
                    </td>
                  </tr>
                ) : sortedAccounts.length === 0 ? (
                  <tr>
                    <td className="px-6 py-12 text-center text-slate-500" colSpan={6}>
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                          <CreditCard className="w-8 h-8 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">No accounts found</p>
                          <p className="text-sm text-slate-500 mt-1">
                            {filterText ? 'Try adjusting your search terms' : 'Get started by adding your first payment account'}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedAccounts.map((acc) => (
                    <tr key={acc.id} className="hover:bg-slate-50 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            {getAccountTypeIcon(acc.account_type)}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{acc.account_name}</p>
                            {acc.description && (
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{acc.description}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                          {acc.account_type?.replace('_', ' ') || "—"}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-700">
                          {acc.account_type === 'upi' && acc.upi_id && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{acc.upi_id}</span>
                              {acc.payment_gateway && (
                                <span className="text-xs text-slate-500">({acc.payment_gateway})</span>
                              )}
                            </div>
                          )}
                          {acc.account_type === 'bank' && acc.bank_name && (
                            <div>
                              <p className="font-medium">{acc.bank_name}</p>
                              {acc.account_number && (
                                <p className="text-xs text-slate-500 mt-0.5">A/C: {acc.account_number}</p>
                              )}
                            </div>
                          )}
                          {acc.account_type === 'card_machine' && acc.machine_provider && (
                            <div>
                              <p className="font-medium">{acc.machine_provider}</p>
                              {acc.terminal_id && (
                                <p className="text-xs text-slate-500 mt-0.5">Terminal: {acc.terminal_id}</p>
                              )}
                            </div>
                          )}
                          {acc.account_type === 'cash_counter' && acc.counter_location && (
                            <p className="font-medium">{acc.counter_location}</p>
                          )}
                          {!acc.upi_id && !acc.bank_name && !acc.machine_provider && !acc.counter_location && (
                            <span className="text-slate-400">—</span>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {acc.is_active ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                            Inactive
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        {acc.is_default ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                            Yes
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>

                      <td className="px-6 py-4 text-right relative">
                        <div className="inline-block action-menu-container">
                          <button
                            data-menu-id={acc.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenActionMenu(
                                openActionMenu === acc.id ? null : acc.id
                              );
                            }}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                          >
                            <MoreVertical size={18} className="text-slate-600" />
                          </button>
                        </div>

                        {openActionMenu === acc.id && (() => {
                          const buttonElement = document.querySelector(`[data-menu-id="${acc.id}"]`);
                          const rect = buttonElement?.getBoundingClientRect();
                          return (
                            <div className="fixed bg-white shadow-xl border border-slate-200 rounded-lg w-40 z-[100] overflow-hidden"
                              style={{
                                top: `${(rect?.bottom || 0) + 8}px`,
                                right: `${window.innerWidth - (rect?.right || 0)}px`
                              }}
                            >
                            <button
                              onClick={() => {
                                setEditItem(acc);
                                setShowForm(true);
                                setOpenActionMenu(null);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-slate-50 text-sm font-medium text-slate-700 flex items-center gap-2 transition-colors"
                            >
                              <Edit2 size={14} />
                              Edit
                            </button>

                            <button
                                onClick={() => handleDeleteClick(acc)}
                                className="w-full text-left px-4 py-3 hover:bg-red-50 text-sm font-medium text-red-600 flex items-center gap-2 transition-colors border-t border-slate-100"
                            >
                            <Trash2 size={14} />
                                   Delete
                            </button>
                          </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results count */}
        {!loading && sortedAccounts.length > 0 && (
          <div className="mt-4 px-2">
            <p className="text-sm text-slate-600">
              Showing <span className="font-semibold text-slate-900">{sortedAccounts.length}</span> of{' '}
              <span className="font-semibold text-slate-900">{accounts.length}</span> accounts
              {filterText && ' (filtered)'}
            </p>
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <PaymentAccountsForm
          item={editItem}
          onClose={() => {
            setShowForm(false);
            setEditItem(null);
          }}
          onSave={handleSave}
        />
      )}
      {/* Delete Dialog */}
{confirmDialog.isOpen && (
  <ConfirmDialog
    isOpen={confirmDialog.isOpen}
    title="Delete Payment Account"
    message="Are you sure you want to delete this payment account?"
    confirmText="Delete"
    type="danger"
    onClose={() =>
      setConfirmDialog({ isOpen: false, data: null })
    }
    onConfirm={deleteNow}
  />
)}

    </div>
  );
}
